/* eslint-disable react-refresh/only-export-components */
/**
 * CEO board panels built on the deal analytics API (one DealFact per live
 * deal, the Pipeline grid's own readers). Each takes the board's cohort, so a
 * "Funded only" or "Andrew Fawer's deals" view cuts every one of them the same
 * way (T-52), and each states the population it reads (T-51).
 */
import { useMemo, useState } from 'react'
import clsx from 'clsx'
import axios from 'axios'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import api from '@/services/api'
import { DASHBOARD_REFETCH_MS, dashboardQueryKey } from '@/components/dashboards'
import FigureValue from '@/components/dashboards/FigureValue'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import { chartPartOfWholeColor } from '@/config/chartTheme'
import PivotTable, { type PivotColumn } from '@/components/analytics/explorer/PivotTable'
import { useDealFacts, usePivot } from '@/components/analytics/explorer/analyticsQueries'
import { useWorkload } from '@/components/analytics/people/TeamWorkload'
import {
  NOT_RECORDED_COLOR,
  cleanFilters,
  compactCurrency,
  describeFilters,
  drillForIds,
  encodeFilters,
  groupLabel,
  measureFigure,
  type AnalyticsFilters,
  type DealFact,
  type PersonAging,
  type PivotRow,
  type WorkloadResponse,
} from '@/services/dashboardAnalytics'
import type { DashboardPayload } from '@/types/dashboards'

// ── Board query with a cohort ──────────────────────────────────────────────

/**
 * The CEO board document, cut to the cohort when there is one.
 *
 * No cohort: `GET /dashboards/ceo` (the kit's endpoint). A cohort: `POST
 * /dashboards/analytics/ceo {filters}` — the same document with every
 * book-derived panel counting only the cohort; finance-close panels (plan,
 * revenue, ROE) are firm records and are not cut (the server says so).
 */
export function useCeoBoard(cohort: AnalyticsFilters) {
  const filters = cleanFilters(cohort)
  const key = encodeFilters(filters)
  const query = useQuery<DashboardPayload>({
    queryKey: [...dashboardQueryKey('ceo'), key || 'all'],
    queryFn: async ({ signal }) => {
      try {
        const response = key
          ? await api.post<DashboardPayload>('/dashboards/analytics/ceo', { filters }, { signal })
          : await api.get<DashboardPayload>('/dashboards/ceo', { signal })
        return response.data
      } catch (error) {
        if (signal.aborted || (axios.isAxiosError(error) && error.code === 'ERR_CANCELED')) {
          throw new DOMException('The board request was aborted.', 'AbortError')
        }
        throw error
      }
    },
    refetchInterval: DASHBOARD_REFETCH_MS,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  })
  return {
    panels: query.data?.panels ?? {},
    partition: query.data?.partition ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    dataUpdatedAt: query.dataUpdatedAt,
  }
}

// ── Counterparties ─────────────────────────────────────────────────────────

const COUNTERPARTY_TABS = [
  { key: 'brokerage', label: 'Brokerages' },
  { key: 'brokerName', label: 'Brokers' },
  { key: 'sponsor', label: 'Sponsors' },
] as const

const COUNTERPARTY_COLUMNS: PivotColumn[] = [
  { key: 'count' },
  { key: 'commitment' },
  { key: 'fundedCount' },
  { key: 'funded', className: 'hidden 2xl:table-cell' },
  { key: 'quoteRatePct' },
]

/** Which brokerages, brokers and sponsors feed the book — by deal flow, not
 *  CRM last contact (T-68, T-74). Names link to the counterparty's profile. */
export function CounterpartyBoard({ cohort }: { cohort: AnalyticsFilters }) {
  const [tab, setTab] = useState<(typeof COUNTERPARTY_TABS)[number]['key']>('brokerage')
  const pivot = usePivot({ dimension: tab, by: 'outcome', filters: cohort })
  const facts = useDealFacts(cohort)
  const factMap = useMemo(() => new Map((facts.data?.deals ?? []).map((fact) => [fact.id, fact])), [facts.data])
  const role = tab === 'brokerName' ? 'broker' : tab
  const rows = pivot.data?.rows ?? []
  const named = rows.filter((row) => row.key != null).length

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/[0.06] px-2 py-1.5">
        {COUNTERPARTY_TABS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            aria-pressed={tab === entry.key}
            onClick={() => setTab(entry.key)}
            className={clsx(
              'rounded-full border px-2 py-0.5 text-[10px] transition-colors',
              tab === entry.key ? 'border-amber-400/35 bg-amber-400/[0.08] text-amber-200' : 'border-amber-400/10 text-stone-500 hover:text-stone-300',
            )}
          >
            {entry.label}
          </button>
        ))}
        <span className="ml-auto font-mono text-[9.5px] text-stone-500">{pivot.data ? `${named} named` : ''}</span>
      </div>
      {pivot.error ? (
        <p className="px-3 py-3 text-[11px] text-red-300">Could not load relationships.</p>
      ) : pivot.isLoading ? (
        <p className="px-3 py-3 text-[11px] text-stone-500">Loading…</p>
      ) : (
        <PivotTable
          rows={rows}
          totals={null}
          dimensionLabel={COUNTERPARTY_TABS.find((entry) => entry.key === tab)?.label.replace(/s$/, '') ?? tab}
          by="outcome"
          facts={factMap}
          scopeLabel={describeFilters(cohort)}
          columns={COUNTERPARTY_COLUMNS}
          showRank
          labelHref={(row) =>
            row.key ? `/dashboards/people?view=person&role=${role}&name=${encodeURIComponent(row.key)}` : null
          }
          className="min-h-0 flex-1"
        />
      )}
    </div>
  )
}

// ── Ladders over the analytics pivot ───────────────────────────────────────

interface LadderProps {
  rows: readonly PivotRow[]
  measure: 'commitment' | 'funded' | 'count'
  scopeLabel: string
  colorFor?: (index: number, total: number) => string
  emptyText: string
}

/** Columns in the dimension's own order (years, bands), each a button that
 *  opens exactly its deals; the count sits under the bar. */
function Ladder({ rows, measure, scopeLabel, colorFor = chartPartOfWholeColor, emptyText }: LadderProps) {
  const { open } = useDrillDown()
  const value = (row: PivotRow) => {
    const raw = (row.measures as unknown as Record<string, unknown>)[measure]
    return typeof raw === 'number' ? raw : 0
  }
  const max = Math.max(0, ...rows.map(value))
  if (rows.length === 0) return <p className="text-[11px] text-stone-500">{emptyText}</p>
  return (
    <div className="flex h-full min-h-[150px] flex-1 flex-col gap-1.5">
      <div className="flex min-h-[90px] flex-1 items-end gap-1.5">
        {rows.map((row, index) => {
          const amount = value(row)
          const label = groupLabel(row.key, row.label)
          const height = max > 0 ? Math.max(amount > 0 ? 3 : 0, (amount / max) * 100) : 0
          return (
            <button
              key={row.key ?? '∅'}
              type="button"
              onClick={() => open(drillForIds(row.measures.dealIds, `${scopeLabel} — ${label}`))}
              disabled={row.measures.count === 0}
              title={`${label}: ${row.measures.count} deals · ${measure === 'count' ? '' : compactCurrency(amount)}`}
              className="group flex h-full min-w-0 flex-1 flex-col justify-end rounded-sm hover:bg-amber-400/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default"
            >
              <span className="mb-1 truncate text-center font-mono text-[9px] tabular-nums text-stone-400 group-hover:text-amber-200">
                {measure === 'count' ? row.measures.count : compactCurrency(amount)}
              </span>
              <span
                className="w-full rounded-t-sm"
                style={{ height: `${height}%`, background: row.key == null ? NOT_RECORDED_COLOR : colorFor(index, rows.length) }}
              />
            </button>
          )
        })}
      </div>
      <div className="flex flex-shrink-0 gap-1.5">
        {rows.map((row) => (
          <div key={row.key ?? '∅'} className="min-w-0 flex-1 text-center">
            <div className={clsx('truncate font-mono text-[9px] uppercase tracking-wider', row.key == null ? 'text-stone-600' : 'text-stone-500')}>
              {groupLabel(row.key, row.label)}
            </div>
            <div className="font-mono text-[10.5px] tabular-nums text-stone-200">{row.measures.count}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Maturity ladder by year, over the funded deals in the cohort. Maturity =
 * closing (funded) date + initial term months (settled 2 Nov); a funded deal
 * with no term has no maturity and is counted under "Not recorded", never
 * placed in a year.
 */
export function MaturityLadder({ cohort }: { cohort: AnalyticsFilters }) {
  const filters = useMemo(() => ({ ...cleanFilters(cohort), outcome: ['funded'] }), [cohort])
  const pivot = usePivot({ dimension: 'maturityYear', filters })
  const rows = pivot.data?.rows ?? []
  const dated = rows.filter((row) => row.key != null)
  const undated = rows.find((row) => row.key == null)
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      {pivot.isLoading ? (
        <p className="text-[11px] text-stone-500">Loading…</p>
      ) : (
        <Ladder
          rows={dated}
          measure="funded"
          scopeLabel="Funded loans maturing"
          emptyText="No funded loan in this cohort carries a closing date and an initial term."
        />
      )}
      {undated && (
        <p className="flex-shrink-0 text-[9.5px] text-stone-500">
          <FigureValue
            figure={measureFigure(undated.measures, 'count', { title: 'Funded loans with no maturity', label: 'No maturity' })}
          />{' '}
          funded loan{undated.measures.count === 1 ? '' : 's'} carry no closing date or no initial term, so no maturity is stated.
        </p>
      )}
    </div>
  )
}

/** Leverage profile: total commitment by LTV-else-LTC band over the cohort,
 *  one row per band so every band name stays in view however short the panel;
 *  the unknown band is its own row so coverage is visible. Each row opens
 *  exactly its deals. */
export function LeverageProfile({ cohort }: { cohort: AnalyticsFilters }) {
  const { open } = useDrillDown()
  const pivot = usePivot({ dimension: 'ltvBand', filters: cohort })
  const rows = pivot.data?.rows ?? []
  if (pivot.isLoading) return <p className="text-[11px] text-stone-500">Loading…</p>
  if (rows.length === 0) return <p className="text-[11px] text-stone-500">No deal in this cohort.</p>
  const amount = (row: PivotRow) => row.measures.commitment ?? 0
  const max = Math.max(0, ...rows.map(amount))
  const scope = `${describeFilters(cohort)} · LTV/LTC`
  return (
    <ul className="space-y-0.5" aria-label="Total commitment by LTV / LTC band">
      {rows.map((row, index) => {
        const label = groupLabel(row.key, row.label)
        return (
          <li key={row.key ?? '∅'}>
            <button
              type="button"
              onClick={() => open(drillForIds(row.measures.dealIds, `${scope} — ${label}`))}
              disabled={row.measures.count === 0}
              className="grid w-full grid-cols-[minmax(0,max-content)_minmax(60px,1fr)_60px_32px] items-center gap-2 rounded px-0.5 text-left hover:bg-amber-400/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50 disabled:cursor-default"
              title={`${label}: ${row.measures.count} deals · ${compactCurrency(amount(row))}`}
            >
              <span className={clsx('w-[118px] whitespace-nowrap font-mono text-[10px]', row.key == null ? 'text-stone-500' : 'text-stone-300')}>{label}</span>
              <span className="h-2.5 rounded-sm bg-white/[0.03]">
                <span
                  className="block h-full rounded-sm"
                  style={{
                    width: `${max > 0 ? Math.max(amount(row) > 0 ? 2 : 0, (amount(row) / max) * 100) : 0}%`,
                    background: row.key == null ? NOT_RECORDED_COLOR : chartPartOfWholeColor(index, rows.length),
                  }}
                />
              </span>
              <span className="text-right font-mono text-[10.5px] tabular-nums text-stone-200">{compactCurrency(amount(row))}</span>
              <span className="text-right font-mono text-[10px] tabular-nums text-stone-500">{row.measures.count}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

// ── Desk aging watch ───────────────────────────────────────────────────────

interface WatchLine {
  key: string
  label: string
  hint: string
  ids: string[]
  tone: 'warning' | 'negative' | 'neutral'
}

/** Pure: desk-wide aging and staffing totals from the workload document —
 *  each person's server-built id lists unioned (a deal is counted once).
 *  Tested. */
export function deskWatch(data: WorkloadResponse | null | undefined): WatchLine[] {
  if (!data) return []
  const union = (pick: (aging: PersonAging) => string[] | undefined) => {
    const ids = new Set<string>()
    for (const person of data.people) for (const id of (person.aging && pick(person.aging)) ?? []) ids.add(id)
    if (data.unassigned?.aging) for (const id of pick(data.unassigned.aging) ?? []) ids.add(id)
    return [...ids]
  }
  const policy = data.policy ?? {}
  return [
    {
      key: 'quoteSla',
      label: `Not quoted within ${policy.quoteSlaDays ?? 3} days of receipt`,
      hint: 'T-72 quote SLA — 24-hour days, weekends count',
      ids: union((aging) => aging.quoteSlaBreachIds),
      tone: 'negative',
    },
    {
      key: 'triageAged',
      label: `In triage ${policy.triageAgedDays ?? 14}+ days`,
      hint: 'T-73 — neither quoted nor killed',
      ids: union((aging) => aging.triageAgedIds),
      tone: 'warning',
    },
    {
      key: 'quotesAged',
      label: `Quotes out over ${policy.quoteAgedDays ?? 14} days`,
      hint: 'T-74 — no term sheet and no kill',
      ids: union((aging) => aging.quotesOutAgedIds),
      tone: 'warning',
    },
    {
      key: 'staffing',
      label: 'Open deals needing staffing',
      hint: data.staffing?.basis ?? 'No analyst and no secondary underwriter (T-58)',
      ids: data.staffing?.needsStaffing?.dealIds ?? [],
      tone: 'neutral',
    },
  ]
}

/** The desk's response and aging watch — what the CEO would ask Randy about
 *  first. Each line opens exactly its deals. */
export function AgingWatch({ cohort }: { cohort: AnalyticsFilters }) {
  const { open } = useDrillDown()
  const workload = useWorkload(cohort)
  const lines = deskWatch(workload.data)
  if (workload.isLoading) return <p className="text-[11px] text-stone-500">Loading…</p>
  if (workload.error) return <p className="text-[11px] text-red-300">Aging watch unavailable.</p>
  return (
    <ul className="space-y-1">
      {lines.map((line) => (
        <li key={line.key}>
          <button
            type="button"
            onClick={() => open(drillForIds(line.ids, line.label))}
            disabled={line.ids.length === 0}
            title={line.hint}
            className="flex w-full items-center gap-2 rounded-md border border-amber-400/[0.07] bg-[#0a0a0a] px-2 py-1.5 text-left transition-colors hover:border-amber-400/25 disabled:cursor-default disabled:hover:border-amber-400/[0.07]"
          >
            <span
              className={clsx(
                'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                line.ids.length === 0 ? 'bg-stone-700' : line.tone === 'negative' ? 'bg-red-400' : line.tone === 'warning' ? 'bg-amber-400' : 'bg-stone-400',
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-[11px] text-stone-300">{line.label}</span>
            <span
              className={clsx(
                'font-mono text-[13px] font-semibold tabular-nums',
                line.ids.length === 0 ? 'text-stone-600' : line.tone === 'negative' ? 'text-red-300' : line.tone === 'warning' ? 'text-amber-200' : 'text-stone-100',
              )}
            >
              {line.ids.length}
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/** Deals in the cohort that are on the board but not on the map: every
 *  non-dead deal in the cohort less every deal a city point carries. Used for
 *  the map's "Not on the map (n)" drill when the server ships no id list. */
export function unmappedIds(facts: readonly DealFact[], mapped: ReadonlySet<string>): string[] {
  return facts.filter((fact) => fact.outcome !== 'dead' && !mapped.has(fact.id)).map((fact) => fact.id)
}
